import { waitFor } from '@bioliberty/core';
import { HttpStatus } from '@bioliberty/http';
import type { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { isRejectedWithValue } from '@reduxjs/toolkit';
import posthog, { Properties } from 'posthog-js';

// This middleware captures errors from rejected RTK fetch queries
// and logs them to the console and PostHog.
export const errorMiddleware: Middleware = (_api: MiddlewareAPI) => (next) => (action) => {
  if (isRejectedWithValue(action) && isRTKQueryAction(action)) {
    const { method, url } = action.meta.baseQueryMeta.request;
    const { status, data } = action.payload;
    const path = new URL(url).pathname;
    const message = data?.message || 'Unknown error';

    if (shouldCaptureError({ method, path, status, message })) {
      const error = new Error(`API error (${status}): ${method} ${path} - ${message}`);
      captureError(error, { endpointName: action.meta.arg?.endpointName, status, path, method });
    }

    if (!shouldPropagateError({ status })) {
      return;
    }
  }

  try {
    return next(action);
  } catch (error) {
    captureError(error, { actionType: isPlainAction(action) ? action.type : undefined });
    throw error;
  }
};

// Never forward raw request/response bodies to PostHog — RTK Query rejected actions carry the
// original request args (`meta.arg.originalArgs`), which for endpoints like verify-mfa or
// confirm-reset-password includes the live OTP code / password. Only forward the small,
// non-sensitive property bag built by the callers above.
const captureError = async (error: unknown, additionalProperties: Properties) => {
  // Posthog may not be loaded yet if an error occurs during sign in.
  await waitFor(() => posthog.__loaded);
  console.error(error);
  posthog.captureException(error, additionalProperties);
};

const shouldCaptureError = ({
  path,
  status
}: {
  method: string;
  path: string;
  status: number | string;
  message?: string;
}): boolean => {
  return (
    // Thrown when a fetch is cancelled mid-flight e.g. the user signed out.
    !isFetchError({ status }) &&
    // The application handles unauthorized errors.
    status !== HttpStatus.Unauthorized &&
    // The application handles not found errors.
    status !== HttpStatus.NotFound &&
    // A wrong code or lockout on MFA verification is expected user behaviour, not an exception.
    !(status === HttpStatus.Forbidden && path.endsWith('authorize/token/verify'))
  );
};

const shouldPropagateError = ({ status }: { status: number | string }): boolean => {
  return !isFetchError({ status });
};

const isFetchError = ({ status }: { status: number | string }): boolean => {
  return status === 'FETCH_ERROR';
};

const isRTKQueryAction = (obj: object): obj is RTKQueryAction => {
  const action = obj as { payload: { status?: number }; meta: { baseQueryMeta?: object } };
  return 'payload' in action && 'status' in action.payload && 'meta' in action && 'baseQueryMeta' in action.meta;
};

const isPlainAction = (obj: unknown): obj is { type: string } =>
  !!obj && typeof obj === 'object' && 'type' in obj && typeof (obj as { type: unknown }).type === 'string';

interface RTKQueryAction {
  payload: {
    status: number;
    data?: { message?: string };
  };
  meta: {
    baseQueryMeta: {
      request: {
        method: string;
        url: string;
      };
    };
    // The RTK Query mutation/query arg — carries the original request args (e.g. `{ code }`,
    // `{ password }`), so only ever read `endpointName` off it, never `originalArgs`.
    arg?: {
      endpointName?: string;
    };
  };
}
